/**
 * Was beim Ersetzen des Grundrisses mit dem alten Inhalt der Etage geschieht (#735).
 *
 * Räume und Gerätepositionen sind in Gebäudekoordinaten des *alten* Plans erfasst. Kommt ein
 * neuer Plan, sind sie keine Aussage mehr über die Wirklichkeit, sondern nur noch Zahlen, die
 * zufällig irgendwo auf dem neuen Bild landen — im Zweifel mitten in der Legende. Deshalb
 * setzt das Ersetzen die Etage zurück.
 *
 * Zurückgesetzt wird so viel wie nötig und so wenig wie möglich:
 *
 *   - Räume werden gelöscht. Ihr Umriss ist der einzige Grund, warum es sie gibt.
 *   - Geräte bleiben, samt Etagenzuordnung. Sie verlieren nur ihre Position und ihren Raum —
 *     jemand ist durch das Gebäude gelaufen, um sie zu erfassen, und das darf ein Bildwechsel
 *     nicht wegwerfen. Sie müssen danach neu auf dem Plan platziert werden.
 *
 * Der Aufrufer bringt die Schreibfunktionen mit, damit dieser Schritt ohne Portal-Kontext
 * prüfbar bleibt.
 */

export interface FloorResetApi {
  deleteSpace: (space: any) => Promise<unknown>;
  updateAsset: (asset: any) => Promise<unknown>;
}

export interface FloorResetResult {
  spacesDeleted: number;
  assetsCleared: number;
  failed: number;
}

/** Trägt das Gerät überhaupt etwas, das der neue Plan ungültig macht? */
const hasPlanBoundLocation = (asset: any): boolean =>
  asset?.x != null || asset?.y != null || asset?.space != null;

/**
 * Räume der Etage löschen und die Plandaten ihrer Geräte leeren.
 *
 * Einzelne Fehlschläge brechen den Durchlauf nicht ab: ein halb zurückgesetzter Stand ist
 * unangenehm, ein Abbruch nach dem dritten von zwanzig Räumen wäre schlimmer. Wie viel nicht
 * durchging, steht in `failed` — der Aufrufer sagt es dem Benutzer.
 */
export async function resetFloorForNewPlan(
  spaces: any[],
  assets: any[],
  api: FloorResetApi,
): Promise<FloorResetResult> {
  const result: FloorResetResult = { spacesDeleted: 0, assetsCleared: 0, failed: 0 };

  for (const space of spaces ?? []) {
    if (!space?._id) continue;
    try {
      await api.deleteSpace(space);
      result.spacesDeleted += 1;
    } catch {
      result.failed += 1;
    }
  }

  for (const asset of assets ?? []) {
    if (!asset?._id || !hasPlanBoundLocation(asset)) continue;
    try {
      // `floor` bleibt stehen: das Gerät ist weiterhin auf dieser Etage, nur nicht mehr an
      // einer bestimmten Stelle.
      await api.updateAsset({ ...asset, x: null, y: null, space: null });
      result.assetsCleared += 1;
    } catch {
      result.failed += 1;
    }
  }

  return result;
}
